import { useState, useRef, useEffect } from 'react';
import { ChevronsUpDown, Check, Plus, MapPin, Loader2, Globe2 } from 'lucide-react';
import { toast } from 'sonner';
import { useLocations } from '../../context/LocationContext';
import { useAuth } from '../../context/AuthContext';

/**
 * Project-switcher style location dropdown for the top bar.
 *
 * Single-location businesses see a static read-only chip.
 * Multi-location: clickable dropdown with "All locations" + each location +
 * an "Add location" CTA (gated by plan cap).
 */
export default function LocationSwitcher() {
  const { locations, activeLocationId, setActiveLocationId, canAddLocation, maxLocations, refresh, isMultiLocation, primaryId } = useLocations();
  const { api } = useAuth();
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  // Close on outside click.
  useEffect(() => {
    function onClick(e) { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setAdding(false); } }
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const active = activeLocationId === 'all'
    ? null
    : locations.find((l) => l.id === activeLocationId) || locations.find((l) => l.is_primary) || locations[0];

  // Single-location: show the location name as a read-only chip with no dropdown.
  if (!isMultiLocation && locations.length <= 1) {
    if (!locations.length) return null;
    const only = locations[0];
    return (
      <div data-testid="location-switcher-single" className="hidden md:inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-full border border-gray-200 dark:border-white/10 bg-white/60 dark:bg-white/[0.04] text-gray-700 dark:text-slate-300">
        <MapPin className="w-3 h-3 text-gray-400 dark:text-slate-500" />
        <span className="truncate max-w-[120px]">{only.name}</span>
      </div>
    );
  }

  const handleAdd = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      await api.post('/locations', { name });
      await refresh();
      setNewName('');
      setAdding(false);
      toast.success(`${name} added`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add location.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div ref={ref} className="relative">
      <button
        data-testid="location-switcher-button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 text-[11px] md:text-xs font-medium px-2.5 py-1 rounded-full border border-gray-200 dark:border-white/10 bg-white/60 dark:bg-white/[0.04] hover:bg-white/80 dark:hover:bg-white/[0.07] text-gray-800 dark:text-slate-200"
        title={active ? `Viewing: ${active.name}` : 'Viewing: All locations'}
      >
        {active ? <MapPin className="w-3 h-3 text-blue-500" /> : <Globe2 className="w-3 h-3 text-purple-500" />}
        <span className="truncate max-w-[140px]">{active ? active.name : 'All locations'}</span>
        <ChevronsUpDown className="w-3 h-3 text-gray-400 dark:text-slate-500" />
      </button>

      {open && (
        <div
          data-testid="location-switcher-menu"
          className="absolute right-0 mt-2 w-72 rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-[#0c0e13] shadow-xl overflow-hidden z-50"
        >
          <div className="px-3 py-2 text-[10px] uppercase tracking-wide text-gray-500 dark:text-slate-500 border-b border-gray-100 dark:border-white/[0.06]">Switch location</div>
          <div className="max-h-72 overflow-y-auto py-1">
            <button
              data-testid="location-switcher-option-all"
              onClick={() => { setActiveLocationId('all'); setOpen(false); }}
              className={`w-full text-left px-3 py-2 flex items-center gap-2 text-sm hover:bg-gray-50 dark:hover:bg-white/[0.05] ${activeLocationId === 'all' ? 'bg-blue-50/50 dark:bg-blue-500/10' : ''}`}
            >
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-500 to-fuchsia-500 flex items-center justify-center flex-shrink-0">
                <Globe2 className="w-3.5 h-3.5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-gray-900 dark:text-white font-medium truncate">All locations</div>
                <div className="text-[11px] text-gray-500 dark:text-slate-500">Aggregate view across {locations.length} locations</div>
              </div>
              {activeLocationId === 'all' && <Check className="w-4 h-4 text-blue-500 flex-shrink-0" />}
            </button>
            {locations.map((loc) => (
              <button
                key={loc.id}
                data-testid={`location-switcher-option-${loc.id}`}
                onClick={() => { setActiveLocationId(loc.id); setOpen(false); }}
                className={`w-full text-left px-3 py-2 flex items-center gap-2 text-sm hover:bg-gray-50 dark:hover:bg-white/[0.05] ${activeLocationId === loc.id ? 'bg-blue-50/50 dark:bg-blue-500/10' : ''}`}
              >
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-3.5 h-3.5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-gray-900 dark:text-white font-medium truncate flex items-center gap-1.5">
                    {loc.name}
                    {loc.is_primary && <span className="text-[9px] uppercase tracking-wide bg-blue-100 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded-full">Primary</span>}
                  </div>
                  <div className="text-[11px] text-gray-500 dark:text-slate-500 truncate">{loc.twilio_number || 'No number yet'}</div>
                </div>
                {activeLocationId === loc.id && <Check className="w-4 h-4 text-blue-500 flex-shrink-0" />}
              </button>
            ))}
          </div>
          <div className="border-t border-gray-100 dark:border-white/[0.06] px-3 py-2">
            {adding ? (
              <div className="flex items-center gap-2">
                <input
                  data-testid="location-switcher-new-name"
                  autoFocus
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); if (e.key === 'Escape') { setAdding(false); setNewName(''); } }}
                  placeholder="Location name (e.g. Brooklyn)"
                  className="flex-1 text-sm rounded-md border border-gray-200 dark:border-white/10 bg-white dark:bg-[#0a0c10] px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500 text-gray-900 dark:text-white"
                />
                <button
                  data-testid="location-switcher-confirm-add"
                  onClick={handleAdd}
                  disabled={busy || !newName.trim()}
                  className="text-xs font-medium px-2.5 py-1 rounded-md bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-50 inline-flex items-center gap-1"
                >
                  {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                </button>
              </div>
            ) : (
              <button
                data-testid="location-switcher-add"
                onClick={() => { if (canAddLocation) setAdding(true); }}
                disabled={!canAddLocation}
                className={`w-full inline-flex items-center justify-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md ${canAddLocation
                  ? 'text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-500/10'
                  : 'text-gray-400 dark:text-slate-500 cursor-not-allowed'}`}
                title={canAddLocation ? 'Add a new location' : `You\u2019ve hit your plan\u2019s limit of ${maxLocations} location${maxLocations === 1 ? '' : 's'}. Upgrade to add more.`}
              >
                <Plus className="w-3.5 h-3.5" />
                {canAddLocation ? 'Add location' : `Limit reached (${locations.length}/${maxLocations})`}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
